export interface SupabaseConfig {
  url?: string | null;
  anonKey?: string | null;
}

export class SupabaseConfigError extends Error {
  readonly missingEnvVars: readonly string[];

  constructor(missingEnvVars: readonly string[]) {
    super(`Missing required environment variables: ${missingEnvVars.join(', ')}`);
    this.name = 'SupabaseConfigError';
    this.missingEnvVars = missingEnvVars;
  }
}

export function validateSupabaseConfig(config: SupabaseConfig): void {
  const requiredEnvVars: Array<[string, string | null | undefined]> = [
    ['VITE_SUPABASE_URL', config.url],
    ['VITE_SUPABASE_ANON_KEY', config.anonKey],
  ];
  const missingEnvVars = requiredEnvVars
    .filter(([, value]) => !value?.trim())
    .map(([key]) => key);

  if (missingEnvVars.length > 0) {
    throw new SupabaseConfigError(missingEnvVars);
  }
}

export interface AppReadinessOptions {
  config: SupabaseConfig;
  checkDatabase: () => Promise<void>;
  onStatus?: (status: string) => void;
}

export interface AppReadinessResult {
  isReady: true;
  databaseAvailable: boolean;
  databaseError?: Error;
}

export interface StartedAppReadiness {
  isReady: true;
  databaseProbe: Promise<AppReadinessResult>;
}

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Unknown database initialization error');
}

/**
 * Validate configuration synchronously, then start the database check as an
 * advisory promise so callers can mount public and auth routes immediately.
 */
export function beginAppReadiness(options: AppReadinessOptions): StartedAppReadiness {
  validateSupabaseConfig(options.config);
  return {
    isReady: true,
    databaseProbe: initializeAppReadiness(options),
  };
}

/**
 * Validate required config, then probe the database without making it a
 * prerequisite for mounting public, auth, or static routes.
 */
export async function initializeAppReadiness({
  config,
  checkDatabase,
  onStatus,
}: AppReadinessOptions): Promise<AppReadinessResult> {
  validateSupabaseConfig(config);
  onStatus?.('Connecting to database...');

  try {
    await checkDatabase();
    return { isReady: true, databaseAvailable: true };
  } catch (error) {
    return {
      isReady: true,
      databaseAvailable: false,
      databaseError: asError(error),
    };
  }
}

/**
 * Advisory reachability probe. anon only has column-level SELECT on
 * displays, so aggregates (select=count) return 401; read one id instead.
 */
export async function probeDatabase(client: {
  from: (table: string) => {
    select: (columns: string) => PromiseLike<{ error: { message: string } | null }> & {
      limit: (n: number) => PromiseLike<{ error: { message: string } | null }>;
    };
  };
}): Promise<void> {
  const { error } = await client.from('displays').select('id').limit(1);
  if (error) {
    throw new Error(`Database connection error: ${error.message}`);
  }
}
